import { useState, useCallback } from 'react'
import { Steps } from 'antd'
import { Modal, Button, message, Alert, Progress } from '@/components/ui'
import { api } from '@/lib/apiClient'
import UploadStep from './steps/UploadStep'
import IdentifyStep from './steps/IdentifyStep'
import OrganizeStep from './steps/OrganizeStep'
import MatchColumnStep from './steps/MatchColumnStep'
import ConfirmationStep from './steps/ConfirmationStep'
import ReviewStep from './steps/ReviewStep'
import type {
  RegistrationField,
  ColumnMapping,
  UniqueIdentifier,
  ImportUploadResponse,
  ImportPreviewResponse,
  ImportExecuteResponse,
  ImportStatusResponse,
  ImportPreviewRow,
  ImportError,
} from '@/pages/events/attendees/types'

const IMPORT_STEPS = [
  { title: 'Upload', key: 'upload' },
  { title: 'Identify', key: 'identify' },
  { title: 'Organize', key: 'organize' },
  { title: 'Match', key: 'match' },
  { title: 'Email', key: 'confirmation' },
  { title: 'Review', key: 'confirm' },
]

export interface ImportWizardModalProps {
  open: boolean
  onClose: () => void
  eventSlug: string
  registrationFields: RegistrationField[]
  onImportComplete?: () => void
}

interface WizardState {
  step: number
  jobId: number | null
  file: File | null
  fileName: string
  fileSize: number
  headers: string[]
  previewData: Record<string, string>[]
  totalRows: number
  encoding: string
  encodingWarning: boolean
  suggestedMapping: ColumnMapping
  columnMapping: ColumnMapping
  uniqueIdentifier: UniqueIdentifier
  updateExisting: boolean
  importTag: string
  sendConfirmation: boolean
  previewRows: ImportPreviewRow[]
  validCount: number
  errorCount: number
  validationErrors: ImportError[]
  isLoading: boolean
  isProcessing: boolean
  importResult: ImportExecuteResponse | null
  progressPercent: number
}

const initialState: WizardState = {
  step: 0,
  jobId: null,
  file: null,
  fileName: '',
  fileSize: 0,
  headers: [],
  previewData: [],
  totalRows: 0,
  encoding: 'UTF-8',
  encodingWarning: false,
  suggestedMapping: {},
  columnMapping: {},
  uniqueIdentifier: 'email',
  updateExisting: false,
  importTag: `Import-${new Date().toISOString().slice(0, 10)}`,
  sendConfirmation: false,
  previewRows: [],
  validCount: 0,
  errorCount: 0,
  validationErrors: [],
  isLoading: false,
  isProcessing: false,
  importResult: null,
  progressPercent: 0,
}

export function ImportWizardModal({
  open,
  onClose,
  eventSlug,
  registrationFields,
  onImportComplete,
}: ImportWizardModalProps) {
  const [state, setState] = useState<WizardState>(initialState)

  const updateState = useCallback((updates: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  const resetWizard = useCallback(() => {
    setState(initialState)
  }, [])

  const handleClose = useCallback(() => {
    resetWizard()
    onClose()
  }, [onClose, resetWizard])

  // Step 1: Upload file
  const handleFileUpload = async (file: File) => {
    updateState({ isLoading: true, file, fileName: file.name, fileSize: file.size })

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<ImportUploadResponse>(
        `/events/${eventSlug}/attendees/import/upload`,
        formData
      )

      if (response.success && response.job_id) {
        updateState({
          jobId: response.job_id,
          headers: response.headers || [],
          previewData: response.preview_data || [],
          totalRows: response.total_rows || 0,
          encoding: response.encoding || 'UTF-8',
          encodingWarning: response.encoding_warning || false,
          suggestedMapping: response.suggested_mapping || {},
          columnMapping: response.suggested_mapping || {},
          isLoading: false,
        })
        message.success('File uploaded successfully')
      } else {
        message.error(response.errors?.join(', ') || 'Failed to upload file')
        updateState({ isLoading: false })
      }
    } catch (error: any) {
      message.error(error?.message || 'Failed to upload file')
      updateState({ isLoading: false })
    }
  }

  const handleFileRemove = () => {
    updateState({
      file: null,
      fileName: '',
      fileSize: 0,
      headers: [],
      previewData: [],
      totalRows: 0,
      jobId: null,
    })
  }

  // Save settings (Steps 2-4, 6)
  const saveSettings = async () => {
    if (!state.jobId) return

    try {
      await api.patch(`/events/${eventSlug}/attendees/import/${state.jobId}/settings`, {
        unique_identifier: state.uniqueIdentifier,
        update_existing: state.updateExisting,
        import_tag: state.importTag,
        send_confirmation: state.sendConfirmation,
        column_mapping: state.columnMapping,
      })
    } catch (error: any) {
      console.error('Failed to save settings:', error)
    }
  }

  // Step 5: Preview with column mapping
  const fetchPreview = async () => {
    if (!state.jobId) return

    updateState({ isLoading: true })

    try {
      const response = await api.post<ImportPreviewResponse>(
        `/events/${eventSlug}/attendees/import/${state.jobId}/preview`,
        { column_mapping: state.columnMapping }
      )

      if (response.success) {
        updateState({
          previewRows: response.preview_rows,
          validCount: response.valid_count,
          errorCount: response.error_count,
          validationErrors: response.validation_errors,
          isLoading: false,
        })
      } else {
        updateState({ isLoading: false })
      }
    } catch (error: any) {
      message.error(error?.message || 'Failed to generate preview')
      updateState({ isLoading: false })
    }
  }

  // Step 7: Execute import
  const executeImport = async () => {
    if (!state.jobId) return

    updateState({ isProcessing: true })

    try {
      const response = await api.post<ImportExecuteResponse>(
        `/events/${eventSlug}/attendees/import/${state.jobId}/execute`,
        { column_mapping: state.columnMapping }
      )

      if (response.success) {
        if (response.async) {
          // Start polling for progress
          pollImportStatus()
        } else {
          // Synchronous import completed
          updateState({
            isProcessing: false,
            importResult: response,
            progressPercent: 100,
          })
          message.success(`Import completed! ${response.created_count || 0} created, ${response.updated_count || 0} updated`)
          onImportComplete?.()
        }
      } else {
        message.error(response.errors?.map(e => e.message || e).join(', ') || 'Import failed')
        updateState({ isProcessing: false })
      }
    } catch (error: any) {
      message.error(error?.message || 'Failed to execute import')
      updateState({ isProcessing: false })
    }
  }

  // Poll for async import status
  const pollImportStatus = async () => {
    if (!state.jobId) return

    const poll = async () => {
      try {
        const response = await api.get<ImportStatusResponse>(
          `/events/${eventSlug}/attendees/import/${state.jobId}/status`
        )

        updateState({
          progressPercent: response.progress_percent,
        })

        if (response.status === 'completed') {
          updateState({
            isProcessing: false,
            importResult: {
              success: true,
              job_id: state.jobId!,
              async: true,
              created_count: response.created_count,
              updated_count: response.updated_count,
              skipped_count: response.skipped_count,
              error_count: response.error_count,
              errors_data: response.errors_data,
            },
          })
          message.success(`Import completed! ${response.created_count} created, ${response.updated_count} updated`)
          onImportComplete?.()
        } else if (response.status === 'failed') {
          updateState({ isProcessing: false })
          message.error('Import failed')
        } else {
          // Continue polling
          setTimeout(poll, 1000)
        }
      } catch (error) {
        console.error('Failed to poll status:', error)
        setTimeout(poll, 2000)
      }
    }

    poll()
  }

  // Navigation
  const handleNext = async () => {
    // Save settings before moving to next step
    if (state.step >= 1 && state.step <= 4) {
      await saveSettings()
    }

    // Fetch preview when entering Match Column step
    if (state.step === 2) {
      await fetchPreview()
    }

    if (state.step < IMPORT_STEPS.length - 1) {
      updateState({ step: state.step + 1 })
    }
  }

  const handleBack = () => {
    if (state.step > 0) {
      updateState({ step: state.step - 1 })
    }
  }

  const handleConfirm = async () => {
    await saveSettings()
    await executeImport()
  }

  const canProceed = (): boolean => {
    switch (state.step) {
      case 0: // Upload
        return !!state.jobId && state.headers.length > 0
      case 1: // Identify
        return true
      case 2: // Organize
        return true
      case 3: // Match Column
        // Check if required fields are mapped
        const mappedFields = Object.values(state.columnMapping).filter(Boolean)
        return mappedFields.length > 0
      case 4: // Confirmation
        return true
      case 5: // Review
        return !state.isProcessing
      default:
        return false
    }
  }

  const getMappedColumnCount = () => {
    return Object.values(state.columnMapping).filter(Boolean).length
  }

  const getSkippedColumnCount = () => {
    return state.headers.length - getMappedColumnCount()
  }

  const renderStepContent = () => {
    switch (state.step) {
      case 0:
        return (
          <UploadStep
            eventSlug={eventSlug}
            file={state.file}
            fileName={state.fileName}
            fileSize={state.fileSize}
            isLoading={state.isLoading}
            encodingWarning={state.encodingWarning}
            encoding={state.encoding}
            onFileSelect={handleFileUpload}
            onFileRemove={handleFileRemove}
          />
        )
      case 1:
        return (
          <IdentifyStep
            uniqueIdentifier={state.uniqueIdentifier}
            onChange={(value) => updateState({ uniqueIdentifier: value })}
          />
        )
      case 2:
        return (
          <OrganizeStep
            updateExisting={state.updateExisting}
            onChange={(value) => updateState({ updateExisting: value })}
          />
        )
      case 3:
        return (
          <MatchColumnStep
            headers={state.headers}
            previewData={state.previewData}
            columnMapping={state.columnMapping}
            registrationFields={registrationFields}
            onColumnMappingChange={(mapping) => updateState({ columnMapping: mapping })}
            mappedCount={getMappedColumnCount()}
            skippedCount={getSkippedColumnCount()}
          />
        )
      case 4:
        return (
          <ConfirmationStep
            sendConfirmation={state.sendConfirmation}
            onChange={(value) => updateState({ sendConfirmation: value })}
          />
        )
      case 5:
        return (
          <ReviewStep
            totalRows={state.totalRows}
            uniqueIdentifier={state.uniqueIdentifier}
            mappedCount={getMappedColumnCount()}
            skippedCount={getSkippedColumnCount()}
            updateExisting={state.updateExisting}
            importTag={state.importTag}
            sendConfirmation={state.sendConfirmation}
            isProcessing={state.isProcessing}
            progressPercent={state.progressPercent}
            importResult={state.importResult}
          />
        )
      default:
        return null
    }
  }

  const renderFooter = () => {
    if (state.importResult) {
      return (
        <div className="flex justify-end gap-2">
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        </div>
      )
    }

    return (
      <div className="flex justify-between">
        <Button
          variant="tertiary"
          onClick={state.step === 0 ? handleClose : handleBack}
          disabled={state.isLoading || state.isProcessing}
        >
          {state.step === 0 ? 'Cancel' : 'Back'}
        </Button>
        <div className="flex gap-2">
          {state.step === IMPORT_STEPS.length - 1 ? (
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canProceed() || state.isLoading || state.isProcessing}
            >
              {state.isProcessing ? 'Importing...' : 'Complete Import'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canProceed() || state.isLoading}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal
      title="Import Attendees"
      open={open}
      onClose={handleClose}
      width={800}
      footer={renderFooter()}
      closable={!state.isProcessing}
      maskClosable={!state.isProcessing}
    >
      <div className="mb-6 w-full">
        <Steps
          current={state.step}
          size="small"
          items={IMPORT_STEPS.map((step) => ({ title: step.title }))}
        />
      </div>

      <div className="min-h-[300px] w-full">
        {renderStepContent()}
      </div>
    </Modal>
  )
}
